import type { TargetFrame } from "./target-frame"

// Is the car framed the way this shot needs? Boxes are normalized (0–1) to
// the VISIBLE frame (what the person sees). Pure; unit-tested.
//
//   wide    the real car (its traced outline, else the detector's box)
//           whole, inside the safe area, filling the target frame
//           (target-frame.ts) and centred in it. One thing to fix at a time,
//           most important first.
//   closer  the car big enough that panels fill it (cut off is fine)
//   others  no car rule (close-ups and the VIN are judged on focus/light)

export interface Box {
  x: number
  y: number
  w: number
  h: number
}

export type FitIssue =
  | "no-car"
  | "step-back"
  | "come-closer"
  | "aim-left"
  | "aim-right"
  | "aim-up"
  | "aim-down"

export interface Fit {
  pass: boolean
  issue: FitIssue | null
  // Step back: how many times too big the car is for the room it has.
  over?: number
}

export const FRAMING = {
  EDGE: 0.015, // a box this close to an edge counts as cut off
  CLOSER_MIN: 0.9,
  // How far the car's centre may sit from the target's (share of the screen).
  CENTER_DX: 0.06,
  CENTER_DY: 0.08,
  // Once it fits, it may drift this much more before it stops fitting
  // (a hand sways; the lock mustn't flicker).
  HOLD: 1.25,
  // A car running off all four sides is at least this many times too big
  // (its real size can't be seen): "a few steps".
  WAY_OVER: 1.8,
} as const

export function isClipped(b: Box): boolean {
  const e = FRAMING.EDGE
  return b.x < e || b.y < e || b.x + b.w > 1 - e || b.y + b.h > 1 - e
}

// The car's shape (width over height) and the screen's, for sizing up a
// car that runs off both sides.
export interface Shapes {
  car: number
  screen: number
}

// How many times too big the car is for the room it has (1 = just fits). A
// car running off both sides is wider than its box shows: its width comes
// from its height (whole) and a car's shape.
export function overBy(car: Box, room: Box, shapes?: Shapes): number {
  const e = FRAMING.EDGE
  const offSides = car.x < e && car.x + car.w > 1 - e
  const offEnds = car.y < e && car.y + car.h > 1 - e
  if (offSides && offEnds) return FRAMING.WAY_OVER
  const w = offSides && shapes ? Math.max(car.w, (car.h * shapes.car) / shapes.screen) : car.w
  const h = offEnds && shapes ? Math.max(car.h, (car.w * shapes.screen) / shapes.car) : car.h
  return Math.max(w / Math.max(room.w, 1e-6), h / Math.max(room.h, 1e-6))
}

// The whole car for a corner photo.
export function judgeFit(car: Box | null, clipped: boolean, target: TargetFrame, wasPassing: boolean, shapes?: Shapes): Fit {
  if (!car) return { pass: false, issue: "no-car" }
  const k = wasPassing ? FRAMING.HOLD : 1
  const { safe, box } = target
  // Cut off, or bigger than the screen allows: back up.
  if (clipped || isClipped(car) || car.w > safe.w * k || car.h > safe.h * k) {
    return { pass: false, issue: "step-back", over: overBy(car, safe, shapes) }
  }
  // Running past one side: aim that way.
  const slack = wasPassing ? 0.02 : 0
  if (car.x < safe.x - slack) return { pass: false, issue: "aim-left" }
  if (car.x + car.w > safe.x + safe.w + slack) return { pass: false, issue: "aim-right" }
  if (car.y < safe.y - slack) return { pass: false, issue: "aim-up" }
  if (car.y + car.h > safe.y + safe.h + slack) return { pass: false, issue: "aim-down" }
  // Too small for a good photo.
  if (car.w < (box.w * target.minFill) / k) return { pass: false, issue: "come-closer" }
  // Centred in the target.
  const dx = car.x + car.w / 2 - (box.x + box.w / 2)
  const dy = car.y + car.h / 2 - (box.y + box.h / 2)
  if (Math.abs(dx) > FRAMING.CENTER_DX * k) return { pass: false, issue: dx < 0 ? "aim-left" : "aim-right" }
  if (Math.abs(dy) > FRAMING.CENTER_DY * k) return { pass: false, issue: dy < 0 ? "aim-up" : "aim-down" }
  return { pass: true, issue: null }
}

// Closer to the damage: panels should fill the picture (the car cut off is
// exactly right; too close for the detector to see a car at all is fine).
export function judgeCloser(box: Box | null): Fit {
  if (box && !isClipped(box) && box.w < FRAMING.CLOSER_MIN) return { pass: false, issue: "come-closer" }
  return { pass: true, issue: null }
}
